import { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/**
 * ScrollingTalon
 * The real red Honda Talon 1000 (transparent PNG) rides across the bottom of the
 * viewport as you scroll. Reads the real document scroll position every frame
 * (robust with Lenis). Full page scroll (5%->95%) maps to full viewport width.
 * Scroll down => left->right; scroll up => reverses. Mobile + desktop.
 */
export default function ScrollingTalon() {
  const progress = useMotionValue(0);
  const smooth = useSpring(progress, {
    stiffness: 120,
    damping: 26,
    mass: 0.35,
    restDelta: 0.0005,
  });

  useEffect(() => {
    let raf;
    const update = () => {
      let p = 0;
      const l = window.__lenis;
      if (l && typeof l.progress === "number" && !Number.isNaN(l.progress)) {
        p = l.progress;
      } else {
        const doc = document.documentElement;
        const max = (doc.scrollHeight || document.body.scrollHeight) - window.innerHeight;
        p = max > 0 ? window.scrollY / max : 0;
      }
      progress.set(Math.min(1, Math.max(0, p)));
      raf = requestAnimationFrame(update);
    };
    raf = requestAnimationFrame(update);
    return () => cancelAnimationFrame(raf);
  }, [progress]);

  const x = useTransform(smooth, [0.03, 0.97], ["100vw", "-16vw"]);
  const opacity = useTransform(smooth, [0, 0.05, 0.09, 0.93, 1], [0, 0, 1, 1, 0]);
  const bob = useTransform(smooth, (v) => Math.sin(v * Math.PI * 24) * 2.5);

  return (
    <motion.div
      aria-hidden
      className="pointer-events-none fixed bottom-1 md:bottom-2 left-0 right-0 z-30 overflow-x-hidden"
      style={{ opacity }}
      data-testid="scrolling-talon"
    >
      <motion.div
        style={{ x }}
        className="relative w-[160px] md:w-[240px] will-change-transform"
      >
        {/* trailing dust puffs (behind vehicle, to the right as it moves left) */}
        <motion.div
          style={{ y: bob }}
          className="absolute bottom-2 right-[-26px] md:right-[-40px] flex items-end gap-1 flex-row-reverse"
        >
          {[0.4, 0.55, 0.75].map((o, i) => (
            <span
              key={i}
              className="block rounded-full bg-white/15"
              style={{
                width: `${i === 0 ? 18 : 14 - i * 3}px`,
                height: `${i === 0 ? 18 : 14 - i * 3}px`,
                opacity: o,
                filter: "blur(2px)",
              }}
            />
          ))}
        </motion.div>

        {/* speed lines (trailing, to the right) */}
        <div className="absolute bottom-6 md:bottom-9 right-[-34px] md:right-[-54px] flex flex-col gap-1 items-end">
          <span className="block h-[2px] w-7 md:w-12 bg-[color:var(--primary)]/70" />
          <span className="block h-[2px] w-4 md:w-8 bg-[color:var(--primary)]/40 mr-1.5" />
          <span className="block h-[2px] w-9 md:w-14 bg-white/30 mr-1" />
        </div>

        <motion.img
          src="/talon-sprite.png?v=4"
          alt=""
          style={{ y: bob }}
          className="w-full h-auto drop-shadow-[0_12px_16px_rgba(0,0,0,0.45)] select-none"
          draggable={false}
        />
      </motion.div>
    </motion.div>
  );
}
